/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

/*
 * The live objects the page modules share.
 *
 * The map builds its popups, the glossary loads its terms, the sharing
 * panel is configured from the map's own settings — and several other
 * modules need those same objects. They were passed around as properties
 * on the `Kontextlab` global, which meant every reader depended on having
 * been loaded after whoever created them.
 *
 * A reader that runs first gets `null` here and can say so, rather than
 * failing on a property of something undefined.
 */

let panel = null;
let manager = null;
let glossary = null;
let social = null;

/**
 * Publish the popup objects the map has built.
 *
 * @param {object|null} popupPanel - The side panel.
 * @param {object|null} popupManager - The anchored window.
 */
export function setPopupInstances(popupPanel, popupManager) {
    panel = popupPanel ?? null;
    manager = popupManager ?? null;
}

/**
 * Read the popup objects.
 *
 * @returns {{panel: object|null, manager: object|null}}
 */
export function getPopupInstances() {
    return { panel, manager };
}

/**
 * Publish the loaded glossary.
 *
 * @param {object|null} instance - The glossary.
 */
export function setGlossary(instance) {
    glossary = instance ?? null;
}

/**
 * Read the glossary.
 *
 * @returns {object|null} The glossary, or nothing before it has loaded.
 */
export function getGlossary() {
    return glossary;
}

/**
 * Publish the sharing panel.
 *
 * @param {object|null} instance - A `Social` instance.
 */
export function setSocialPanel(instance) {
    social = instance ?? null;
}

/**
 * Read the sharing panel.
 *
 * @returns {object|null} The panel, or nothing if the map offers none.
 */
export function getSocialPanel() {
    return social;
}
